// Browser-side audio for the local "acoustic bridge" mode.
// Capture: PC mic -> 16kHz PCM16 base64 (sent to server -> Gemini).
// Playback: Gemini's 24kHz PCM16 base64 -> PC speaker.

const CAPTURE_RATE = 16000;
const PLAYBACK_RATE = 24000;

function floatToPcm16(float: Float32Array): Int16Array {
  const out = new Int16Array(float.length);
  for (let i = 0; i < float.length; i++) {
    let s = Math.max(-1, Math.min(1, float[i]!));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function downsample(input: Float32Array, from: number, to: number): Float32Array {
  if (from === to) return input;
  const ratio = from / to;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = pos - i0;
    out[i] = input[i0]! + (input[i1]! - input[i0]!) * frac;
  }
  return out;
}

function int16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function base64ToInt16(b64: string): Int16Array {
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
  return new Int16Array(buf);
}

export class LocalAudio {
  private captureCtx: AudioContext | null = null;
  private playCtx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private playHead = 0;
  private queued = new Set<AudioBufferSourceNode>();

  async start(onPcm16Base64: (b64: string) => void): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    const Ctx: typeof AudioContext =
      window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.captureCtx = new Ctx();
    this.playCtx = new Ctx();
    this.playHead = 0;

    this.source = this.captureCtx.createMediaStreamSource(this.stream);
    // Smaller frame = lower capture latency (~21ms @ 96k, ~43ms @ 48k).
    this.processor = this.captureCtx.createScriptProcessor(2048, 1, 1);
    const inRate = this.captureCtx.sampleRate;

    this.processor.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      const ds = downsample(new Float32Array(input), inRate, CAPTURE_RATE);
      onPcm16Base64(int16ToBase64(floatToPcm16(ds)));
    };
    this.source.connect(this.processor);
    // ScriptProcessor needs a destination connection to run.
    this.processor.connect(this.captureCtx.destination);
  }

  // Queue Gemini's 24kHz PCM16 for gapless sequential playback.
  playPcm24Base64(b64: string): void {
    if (!this.playCtx) return;
    const pcm = base64ToInt16(b64);
    if (pcm.length === 0) return;
    const buf = this.playCtx.createBuffer(1, pcm.length, PLAYBACK_RATE);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i]! / 0x8000;
    const src = this.playCtx.createBufferSource();
    src.buffer = buf;
    src.connect(this.playCtx.destination);
    const now = this.playCtx.currentTime;
    const startAt = Math.max(now, this.playHead);
    src.start(startAt);
    this.playHead = startAt + buf.duration;
    this.queued.add(src);
    src.onended = () => this.queued.delete(src);
  }

  // Hard-stop everything currently scheduled/playing (barge-in, steer,
  // interruption). Just moving the playhead is NOT enough — already-started
  // source nodes keep playing and overlap the new response (two voices).
  flushPlayback(): void {
    for (const src of this.queued) {
      try {
        src.onended = null;
        src.stop();
        src.disconnect();
      } catch {
        // already stopped
      }
    }
    this.queued.clear();
    this.playHead = this.playCtx ? this.playCtx.currentTime : 0;
  }

  stop(): void {
    try {
      this.flushPlayback();
      this.processor?.disconnect();
      this.source?.disconnect();
      this.stream?.getTracks().forEach((t) => t.stop());
      void this.captureCtx?.close();
      void this.playCtx?.close();
    } catch {
      // ignore
    }
    this.processor = null;
    this.source = null;
    this.stream = null;
    this.captureCtx = null;
    this.playCtx = null;
  }
}
